// Estado de la bandeja de la planta seleccionada (PP-34). Sin websockets ni
// push (PLAN-ASIGNACION-PROVEEDOR §10, v1 es polling, como la torre):
// - encuesta cada 10 s mientras la vista esta abierta y la pestana visible;
// - vuelve a preguntar al recuperar el foco;
// - cuando un reloj de oferta llega a cero, pregunta una vez que paso, y
//   muestra lo que diga el servidor (vencio, paso a otro, se asigno).
//
// Dos lecturas por vuelta (PP-34b): las ofertas vivas de cualquier dia, para
// que ningun plazo corra fuera de la vista, y los viajes del dia elegido. Si
// una de las dos falla, la vuelta entera cuenta como fallida.
//
// Una respuesta vieja (otra planta, otro dia, otra sesion, o una encuesta que
// llego despues de otra mas nueva) se descarta. El error no entra al estado:
// solo su tipo, porque el cuerpo remoto puede traer datos.
import { useCallback, useEffect, useRef, useState } from "react";
import type { ProviderTrip } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { dueDeadlines, mergeInbox } from "../logic/requests.js";
import type { Clock } from "./clock.js";
import { RequestsError, type RequestsPort, type RequestsSite } from "./port.js";

export const POLL_MS = 10_000;
const TICK_MS = 1_000;

export type InboxState =
  | { estado: "cargando" }
  | { estado: "denegado" }
  | { estado: "error" }
  | {
      estado: "listo";
      date: string;
      timezone: string;
      items: readonly ProviderTrip[];
      /** Hay mas viajes que los que entraron en la consulta. */
      truncated: boolean;
      /** La ultima actualizacion fallo: se muestra lo anterior y se reintenta en la proxima vuelta. */
      stale: boolean;
    };

export interface InboxHandle {
  state: InboxState;
  /** Instante del reloj con el que se pinta la cuenta regresiva. */
  now: number;
  refresh: () => void;
}

function hidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export function useInbox(
  port: RequestsPort,
  session: PortalSession,
  site: RequestsSite,
  date: string | undefined,
  clock: Clock,
): InboxHandle {
  const [state, setState] = useState<InboxState>({ estado: "cargando" });
  const [now, setNow] = useState(() => clock.now());
  const sequence = useRef(0);
  const fired = useRef(new Set<string>());
  const { siteId, providerId } = site;

  const refresh = useCallback(() => {
    sequence.current += 1;
    const mine = sequence.current;
    const target = { siteId, providerId };
    Promise.all([port.offers(session, target), port.list(session, target, date)])
      .then(([live, data]) => {
        if (mine !== sequence.current) return;
        setState({
          estado: "listo",
          date: data.date,
          timezone: data.timezone,
          items: mergeInbox(live.items, data.items),
          truncated: data.nextCursor !== undefined || live.nextCursor !== undefined,
          stale: false,
        });
      })
      .catch((error: unknown) => {
        if (mine !== sequence.current) return;
        if (error instanceof RequestsError && error.kind === "access") {
          setState({ estado: "denegado" });
          return;
        }
        setState((previous) => (previous.estado === "listo" ? { ...previous, stale: true } : { estado: "error" }));
      });
  }, [port, session, siteId, providerId, date]);

  // Carga inicial y cada vez que cambia la planta, el dia o la sesion.
  useEffect(() => {
    setState({ estado: "cargando" });
    fired.current = new Set();
    refresh();
    return () => {
      // Lo que llegue despues del desmontaje o del cambio no toca el estado.
      sequence.current += 1;
    };
  }, [refresh]);

  // Encuesta periodica, en pausa con la pestana oculta.
  useEffect(
    () =>
      clock.every(POLL_MS, () => {
        if (!hidden()) refresh();
      }),
    [clock, refresh],
  );

  // Volver a la pestana o a la ventana: preguntar de nuevo.
  useEffect(() => {
    const onVisible = () => {
      if (!hidden()) refresh();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  // Segundero de la cuenta regresiva.
  useEffect(() => {
    setNow(clock.now());
    return clock.every(TICK_MS, () => setNow(clock.now()));
  }, [clock]);

  // Un reloj que llega a cero: una sola consulta por reloj. Si el servidor
  // extiende o cambia la oferta, la clave cambia y vuelve a valer.
  useEffect(() => {
    if (state.estado !== "listo") return;
    const due = dueDeadlines(state.items, now).filter((key) => !fired.current.has(key));
    if (due.length === 0) return;
    for (const key of due) fired.current.add(key);
    refresh();
  }, [state, now, refresh]);

  return { state, now, refresh };
}
